import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ArrowRight } from "lucide-react";

import PortfolioMapGuideCard from "./PortfolioMapGuideCard";
import PortfolioMapGuidePanel from "./PortfolioMapGuidePanel";
import {
  guideColorWash,
  PM_GUIDES,
  type GuideCell,
} from "./portfolioMapGuides";
import PortfolioMapOverlapModal from "./PortfolioMapOverlapModal";
import {
  accountInitials,
  BH_CLASS_FILL,
  BH_CLASS_STROKE,
  dotRadius,
  type EnrichedMapAccount,
  evenTicks,
  normalizeIds,
  RL_PRIORITY_LABEL,
  TIER_HALO,
  tierBreakdown,
} from "./portfolioMapVisualHelpers";
import { useSignalTitle } from "./useSignalTitle";

/** Lower = more urgent — used to pick the visible account in a stack. */
const TIER_URGENCY: Record<string, number> = {
  critical: 0,
  warning: 1,
  opp: 2,
  maint: 3,
};

function scoreClusterKey(x: number, y: number): string {
  // Same Fit/score (1 decimal) → one stack; avoids float noise.
  return `${x.toFixed(1)}|${y.toFixed(1)}`;
}

function pickStackRepresentative(
  accounts: EnrichedMapAccount[]
): EnrichedMapAccount {
  return [...accounts].sort((a, b) => {
    const ta = TIER_URGENCY[a.top_signal_tier ?? ""] ?? 9;
    const tb = TIER_URGENCY[b.top_signal_tier ?? ""] ?? 9;
    return ta - tb || a.account_name.localeCompare(b.account_name);
  })[0]!;
}

type ScatterPoint = {
  account: EnrichedMapAccount;
  x: number;
  y: number;
  /** All accounts at this Fit/score position (includes `account`). */
  stack: EnrichedMapAccount[];
};

export interface LensConfig {
  key: string;
  label: string;
  /** Verbatim PM_LENS_AXIS text for this lens (mock ~6640-6647), "Y = ... · X = ..." order. */
  axisDescription: string;
  xAxisTitle: string;
  getX: (a: EnrichedMapAccount) => number | null;
  xMin: number;
  xMax: number;
  xMid: number;
  /** Mock's xLowLabel/xHighLabel (pmDrawScatter data-axis-end text, ~5790-5791). */
  xLowLabel: string;
  xHighLabel: string;
  /** Left-gutter label when X is missing (default "X UNAVAILABLE"). */
  xUnknownLabel?: string;
  yLabel: string;
  yAxisTitle: string;
  getY: (a: EnrichedMapAccount) => number | null;
  yMid: number;
  yMin: number;
  yMax: number;
  /** Mock's yLowLabel/yHighLabel (pmDrawScatter data-axis-y-end text, ~5794-5795). */
  yLowLabel: string;
  yHighLabel: string;
  quadrants: {
    topRight: { label: string; blurb: string };
    topLeft: { label: string; blurb: string };
    bottomRight: { label: string; blurb: string };
    bottomLeft: { label: string; blurb: string };
  };
  /** Optional in-plot corner titles (Birds Eye Risk–Opp style). */
  quadrantCornerLabels?: {
    topLeft: { title: string; color: string; subtitle?: string };
    topRight: { title: string; color: string; subtitle?: string };
    bottomLeft: { title: string; color: string; subtitle?: string };
    bottomRight: { title: string; color: string; subtitle?: string };
  };
}

// Mock renderPMBHFit / pmDrawScatter (my-accounts-portfolio-v4.html ~6028-6036,
// ~5798): fixed 760×400 viewBox with these pads — keep circles round via
// aspect-ratio on the wrap + preserveAspectRatio meet (not "none" stretch).
const PLOT_W = 760;
const PLOT_H = 400;
/** Extra top pad leaves room for the in-chart View Guide control. */
const PLOT_PAD = { L: 78, R: 20, T: 34, B: 68 } as const;
/** Inset the rotated Y-axis title from the SVG/container left edge. */
const Y_AXIS_TITLE_INSET = 8;
/** Axis spine + filled △ — lighter than midlines so it doesn't dominate. */
const AXIS_STROKE = "#d1d5db";
const AXIS_ARROW_LEN = 7;
const AXIS_ARROW_HALF = 3.5;
/** Inset for end-anchored max tick + caption (High Fit, etc.) from plot right. */
const X_AXIS_EDGE_PAD_R = 8;
/** Space between the four quadrant frames (total; split across abutting edges).
 *  0 so the dashed mid-axis is the only center divider (no colored inner edges). */
const QUADRANT_GAP = 0;
/** Keep frames clear of axis spines / end captions (esp. left Y-axis). */
const QUADRANT_OUTER_INSET = 5;
const QUADRANT_RADIUS = 3;
const QUADRANT_STROKE = 1;
const QUADRANT_STROKE_ACTIVE = 1.5;
/** Soften guide hues so frames read as light accent borders. */
const QUADRANT_BORDER_ALPHA = 0.39;
/** Mid-axis divider (replaces solid colored inner edges at the center). */
const MID_AXIS_DASH = "3 2.5";
const MID_AXIS_STROKE = "#cbd5e1";

interface Props {
  accounts: EnrichedMapAccount[];
  lens: LensConfig;
  selectedAccountIds: string[];
  onSelectedAccountIdsChange: (accountIds: string[]) => void;
  /** When false, dots use a uniform larger radius (Signal service off). */
  signalsVisible?: boolean;
}

const PortfolioMapScatter: React.FC<Props> = ({
  accounts,
  lens,
  selectedAccountIds,
  onSelectedAccountIdsChange,
  signalsVisible = true,
}) => {
  const signalTitle = useSignalTitle();
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(
    null
  );
  type QuadrantKey = "topLeft" | "topRight" | "bottomLeft" | "bottomRight";
  const [activeQuadrant, setActiveQuadrant] = useState<QuadrantKey | null>(
    null
  );
  const [showGuide, setShowGuide] = useState(false);
  const [overlapStack, setOverlapStack] = useState<EnrichedMapAccount[] | null>(
    null
  );

  const selectedIdSet = useMemo(
    () => new Set(selectedAccountIds),
    [selectedAccountIds]
  );

  useEffect(() => {
    setActiveQuadrant(null);
    setOverlapStack(null);
  }, [lens.key]);

  // Clear / Escape / empty-plot click clear selection in the parent — drop
  // the purple quadrant frame too (it lives in local state).
  useEffect(() => {
    if (selectedAccountIds.length === 0) setActiveQuadrant(null);
  }, [selectedAccountIds]);

  useEffect(() => {
    if (!showGuide) return;
    setHoverId(null);
    setHoverPos(null);
  }, [showGuide]);

  const guideCells = PM_GUIDES[lens.key];

  /** Visual quadrant → guide cell (TL, TR, BL, BR). */
  const overlayCells = useMemo(() => {
    if (!guideCells || guideCells.length < 4) return null;
    return {
      topLeft: guideCells[0],
      topRight: guideCells[1],
      bottomLeft: guideCells[2],
      bottomRight: guideCells[3],
    };
  }, [guideCells]);

  /** Soft guide/corner hues for light quadrant borders (no fill wash). */
  const quadrantBorderColors = useMemo(() => {
    const toSoft = (hex: string) => {
      const raw = hex.trim().replace("#", "");
      const normalized =
        raw.length === 3
          ? raw
              .split("")
              .map(c => c + c)
              .join("")
          : raw;
      if (normalized.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(normalized)) {
        return `rgba(148,163,184,${QUADRANT_BORDER_ALPHA})`;
      }
      const r = parseInt(normalized.slice(0, 2), 16);
      const g = parseInt(normalized.slice(2, 4), 16);
      const b = parseInt(normalized.slice(4, 6), 16);
      return `rgba(${r},${g},${b},${QUADRANT_BORDER_ALPHA})`;
    };
    const corners = lens.quadrantCornerLabels;
    if (corners) {
      return {
        topLeft: toSoft(corners.topLeft.color),
        topRight: toSoft(corners.topRight.color),
        bottomLeft: toSoft(corners.bottomLeft.color),
        bottomRight: toSoft(corners.bottomRight.color),
      };
    }
    return {
      topLeft: toSoft("#d97706"),
      topRight: toSoft("#16a34a"),
      bottomLeft: toSoft("#64748b"),
      bottomRight: toSoft("#dc2626"),
    };
  }, [lens.quadrantCornerLabels]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onSelectedAccountIdsChange([]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onSelectedAccountIdsChange]);

  const padR = PLOT_PAD.R;
  const padT = PLOT_PAD.T;
  const padB = PLOT_PAD.B;
  const RW = PLOT_W;
  const RH = PLOT_H;
  /** Left margin for Y-axis title; availability gutter (if any) sits after this. */
  const axisL = PLOT_PAD.L;

  /** AH UNSCORED left gutter is BH×AH only (mock pmDrawScatter bhah label).
   * Other lenses omit the availability bar. */
  const isBhAhLens = lens.key === "bh_ah";
  const showXGutter = isBhAhLens;
  const availability = useMemo(() => {
    if (!showXGutter) {
      return {
        xUnknown: [] as { account: EnrichedMapAccount; yVal: number }[],
        bothUnknown: [] as EnrichedMapAccount[],
      };
    }
    const xUnknown: { account: EnrichedMapAccount; yVal: number }[] = [];
    const bothUnknown: EnrichedMapAccount[] = [];
    accounts.forEach(a => {
      const x = lens.getX(a);
      const y = lens.getY(a);
      const xMiss = x == null || !Number.isFinite(x);
      const yMiss = y == null || !Number.isFinite(y);
      if (xMiss && yMiss) bothUnknown.push(a);
      else if (xMiss) xUnknown.push({ account: a, yVal: y as number });
    });
    return { xUnknown, bothUnknown };
  }, [accounts, lens, showXGutter]);

  const hasXUnknown =
    showXGutter &&
    (availability.xUnknown.length > 0 || availability.bothUnknown.length > 0);
  const xGutterW = hasXUnknown ? 48 : 0;
  // Keep enough gap so end-anchored Y tick labels (0/25/50/75/100) clear
  // the gutter's right edge instead of drawing on top of the bubbles.
  const xGutterGap = hasXUnknown ? 28 : 0;

  const plotL = axisL + xGutterW + xGutterGap;
  const plotT = padT;
  const plotW = RW - plotL - padR;
  const plotH = RH - plotT - padB;
  const plotBottom = plotT + plotH;
  const yTickLabelX = hasXUnknown ? plotL - 6 : plotL - 9;
  const xAxisEdgeMaxX = plotL + plotW - X_AXIS_EDGE_PAD_R;
  /** BH×AH keeps legacy caption placement (AH gutter layout). */
  const yCaptionX = isBhAhLens
    ? hasXUnknown
      ? axisL - 8
      : plotL - 3
    : yTickLabelX;
  const xHighCaptionX = isBhAhLens ? plotL + plotW : xAxisEdgeMaxX;

  const plotX = useCallback(
    (x: number) => plotL + ((x - lens.xMin) / (lens.xMax - lens.xMin)) * plotW,
    [plotL, plotW, lens.xMin, lens.xMax]
  );
  const plotY = useCallback(
    (y: number) =>
      plotBottom - ((y - lens.yMin) / (lens.yMax - lens.yMin)) * plotH,
    [plotBottom, plotH, lens.yMin, lens.yMax]
  );
  const midX = plotX(lens.xMid);
  const midY = plotY(lens.yMid);
  /** White framed boxes sitting inside each quadrant (clip 25/75 gridlines). */
  const quadrantBoxes = useMemo(() => {
    const g = QUADRANT_GAP / 2;
    const o = QUADRANT_OUTER_INSET;
    return [
      {
        key: "topLeft" as const,
        x: plotL + o,
        y: plotT + o,
        w: midX - plotL - g - o,
        h: midY - plotT - g - o,
      },
      {
        key: "topRight" as const,
        x: midX + g,
        y: plotT + o,
        w: plotL + plotW - midX - g - o,
        h: midY - plotT - g - o,
      },
      {
        key: "bottomLeft" as const,
        x: plotL + o,
        y: midY + g,
        w: midX - plotL - g - o,
        h: plotBottom - midY - g - o,
      },
      {
        key: "bottomRight" as const,
        x: midX + g,
        y: midY + g,
        w: plotL + plotW - midX - g - o,
        h: plotBottom - midY - g - o,
      },
    ];
  }, [plotL, plotT, plotW, plotBottom, midX, midY]);

  const xTicks = useMemo(
    () => evenTicks(lens.xMin, lens.xMax),
    [lens.xMin, lens.xMax]
  );
  const yTicks = useMemo(
    () => evenTicks(lens.yMin, lens.yMax),
    [lens.yMin, lens.yMax]
  );

  const points = useMemo((): ScatterPoint[] => {
    const buckets = new Map<
      string,
      { accounts: EnrichedMapAccount[]; xVal: number; yVal: number }
    >();
    accounts.forEach(a => {
      const xVal = lens.getX(a);
      const yVal = lens.getY(a);
      if (xVal == null || yVal == null) return;
      if (!Number.isFinite(xVal) || !Number.isFinite(yVal)) return;
      const key = scoreClusterKey(xVal, yVal);
      const bucket = buckets.get(key);
      if (bucket) bucket.accounts.push(a);
      else buckets.set(key, { accounts: [a], xVal, yVal });
    });
    return Array.from(buckets.values()).map(
      ({ accounts: stack, xVal, yVal }) => {
        const account = pickStackRepresentative(stack);
        return {
          account,
          x: plotX(xVal),
          y: plotY(yVal),
          stack,
        };
      }
    );
  }, [accounts, lens, plotX, plotY]);

  /** Left-gutter placements only (AH unscored / X unknown). No bottom
   * Y-unavailable strip — y-only unknowns are omitted from gutters. */
  const gutterPoints = useMemo(() => {
    type GutterPt = {
      account: EnrichedMapAccount;
      x: number;
      y: number;
      region: "x-unknown";
    };
    const out: GutterPt[] = [];
    const xCollisions: Record<number, number> = {};
    const placeInXGutter = (account: EnrichedMapAccount, baseY: number) => {
      const key = Math.round(baseY / 22);
      const collisionIndex = xCollisions[key] || 0;
      xCollisions[key] = collisionIndex + 1;
      // Keep dots centered in the narrow gutter; slight fan on overlap.
      const cx =
        axisL +
        xGutterW / 2 +
        (collisionIndex === 0 ? 0 : collisionIndex % 2 ? 8 : -8);
      const cy = Math.max(plotT + 16, Math.min(plotBottom - 16, baseY));
      out.push({ account, x: cx, y: cy, region: "x-unknown" });
    };
    availability.xUnknown.forEach(item => {
      placeInXGutter(item.account, plotY(item.yVal));
    });
    // Both-unknown: park at bottom of the AH/X unscored gutter.
    availability.bothUnknown.forEach((account, index) => {
      placeInXGutter(account, plotBottom - 28 - index * 22);
    });
    return out;
  }, [availability, axisL, xGutterW, plotT, plotBottom, plotY]);

  const allHoverable = useMemo(
    () => [...points, ...gutterPoints],
    [points, gutterPoints]
  );
  const hoverPoint =
    allHoverable.find(p => p.account.account_id === hoverId) ?? null;

  const selectXUnknownGutter = useCallback(() => {
    const ids = gutterPoints.map(p => p.account.account_id);
    onSelectedAccountIdsChange(normalizeIds(ids));
  }, [gutterPoints, onSelectedAccountIdsChange]);

  /** Bottom tray for lenses without the AH UNSCORED gutter (BH×Fit, etc.).
   * BH×AH parks missing-AH accounts in the left gutter instead. */
  const unscoredAccounts = useMemo(() => {
    if (showXGutter) return [];
    return accounts.filter(a => {
      const x = lens.getX(a);
      const y = lens.getY(a);
      return (
        x == null || !Number.isFinite(x) || y == null || !Number.isFinite(y)
      );
    });
  }, [accounts, lens, showXGutter]);

  const plotPct = {
    left: `${(plotL / RW) * 100}%`,
    top: `${(plotT / RH) * 100}%`,
    width: `${(plotW / RW) * 100}%`,
    height: `${(plotH / RH) * 100}%`,
  };

  /** Guide overlay — soft wash + light gray border (SVG frames hide while open). */
  const renderQuadrantOverlay = (
    key: QuadrantKey,
    cell: GuideCell,
    position: React.CSSProperties
  ) => (
    <div
      key={key}
      className="pointer-events-none absolute flex flex-col justify-start overflow-hidden"
      style={position}
    >
      <div
        className="min-h-0 flex-1 overflow-hidden rounded-[3px] backdrop-blur-[3px]"
        style={{ background: guideColorWash(cell.color) }}
      >
        <PortfolioMapGuideCard cell={cell} variant="plain" />
      </div>
    </div>
  );

  /** X/Y spines + arrowheads — shared by the plot SVG and the guide top layer. */
  const renderAxisSpines = (stroke: string = AXIS_STROKE) => {
    const plotRight = plotL + plotW;
    const strokeW = 1.2;
    const xBaseX = plotRight - AXIS_ARROW_LEN;
    const yBaseY = plotT + AXIS_ARROW_LEN;
    const xLineEnd = xBaseX - strokeW / 2;
    const yLineEnd = yBaseY + strokeW / 2;
    return (
      <g pointerEvents="none">
        <line
          x1={plotL}
          y1={plotBottom}
          x2={xLineEnd}
          y2={plotBottom}
          stroke={stroke}
          strokeWidth={strokeW}
          strokeLinecap="butt"
        />
        <polygon
          fill={stroke}
          points={[
            `${plotRight},${plotBottom}`,
            `${xBaseX},${plotBottom - AXIS_ARROW_HALF}`,
            `${xBaseX},${plotBottom + AXIS_ARROW_HALF}`,
          ].join(" ")}
        />
        <line
          x1={plotL}
          y1={plotBottom}
          x2={plotL}
          y2={yLineEnd}
          stroke={stroke}
          strokeWidth={strokeW}
          strokeLinecap="butt"
        />
        <polygon
          fill={stroke}
          points={[
            `${plotL},${plotT}`,
            `${plotL - AXIS_ARROW_HALF},${yBaseY}`,
            `${plotL + AXIS_ARROW_HALF},${yBaseY}`,
          ].join(" ")}
        />
      </g>
    );
  };

  return (
    // .pm-scatter-panel: flex-direction:column; gap:8px (mock ~1230-1233).
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      {/* .pm-scatter-wrap: aspect-ratio 760/400, border, radius 10px
          (mock ~1286-1290). Fixed viewBox keeps dots circular. Square
          plot-frame corners (no rx) so spines meet without a corner gap. */}
      <div className="relative w-full overflow-hidden rounded-[10px] border border-gray-200 bg-[#fafbfc] [aspect-ratio:760/400]">
        {guideCells && guideCells.length > 0 && (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              setShowGuide(open => !open);
            }}
            className={`absolute right-2.5 top-2 z-[8] rounded-md border px-2.5 py-1 text-[11.5px] font-semibold shadow-sm ${
              showGuide
                ? "border-brand bg-brand-faint text-brand"
                : "border-gray-200 bg-white text-gray-600 hover:border-purple-300 hover:text-purple-700"
            }`}
          >
            {showGuide ? "Hide guide ▵" : "View Guide ▿"}
          </button>
        )}
        <svg
          className="block h-full w-full select-none"
          viewBox={`0 0 ${RW} ${RH}`}
          preserveAspectRatio="xMidYMid meet"
          onClick={() => onSelectedAccountIdsChange([])}
        >
          {/* Plot frame fill only — borders are the X/Y arrowed spines. */}
          <rect
            x={plotL}
            y={plotT}
            width={plotW}
            height={plotH}
            fill="#fbfcfe"
          />

          {/* Left availability gutter only (e.g. AH UNSCORED). No bottom
              Y-unavailable strip. */}
          {hasXUnknown && (
            <g>
              <rect
                x={axisL}
                y={plotT}
                width={xGutterW}
                height={plotH}
                rx={6}
                fill="#f5f7fa"
                stroke="#dfe5ec"
                strokeWidth={1}
                style={{ cursor: "pointer" }}
                onClick={e => {
                  e.stopPropagation();
                  selectXUnknownGutter();
                }}
              />
              <text
                x={axisL + xGutterW / 2}
                y={RH - 23}
                textAnchor="middle"
                fontSize={7}
                fontWeight={800}
                fill="#9ca3af"
                letterSpacing="0.02em"
                pointerEvents="none"
              >
                {lens.xUnknownLabel ?? "X UNAVAILABLE"}
              </text>
            </g>
          )}

          {/* Tick labels only here — 25/75 gridlines are drawn after the white
              quadrant fills so they stay visible on top of the boxes. */}
          {xTicks.map(tick => {
            const x = plotX(tick);
            const isXMin = tick === lens.xMin;
            const isXMax = tick === lens.xMax;
            const xLabelX = isBhAhLens
              ? x
              : isXMin
                ? plotL
                : isXMax
                  ? xAxisEdgeMaxX
                  : x;
            const xLabelAnchor = isBhAhLens
              ? "middle"
              : isXMin
                ? "start"
                : isXMax
                  ? "end"
                  : "middle";
            return (
              <text
                key={`xt-${tick}`}
                x={xLabelX}
                y={plotBottom + 16}
                textAnchor={xLabelAnchor}
                fontSize={8.5}
                fill="#9ca3af"
              >
                {tick}
              </text>
            );
          })}
          {yTicks.map(tick => {
            const y = plotY(tick);
            return (
              <text
                key={`yt-${tick}`}
                x={yTickLabelX}
                y={y + 3}
                textAnchor="end"
                fontSize={8.5}
                fill="#9ca3af"
              >
                {tick}
              </text>
            );
          })}

          {/* Quadrant fills + outer-colored L borders. Purple active frame
              above mid-axes; Clear empties selection and drops the frame. */}
          {!showGuide &&
            quadrantBoxes.map(q => {
              const isActive = activeQuadrant === q.key;
              const color = quadrantBorderColors[q.key];
              const { x, y, w, h } = q;
              const r = QUADRANT_RADIUS;
              // Outer L only (plot perimeter). Inner mid edges = shared dashes.
              const outerPath =
                q.key === "topLeft"
                  ? `M ${x} ${y + h} L ${x} ${y + r} Q ${x} ${y} ${x + r} ${y} L ${x + w} ${y}`
                  : q.key === "topRight"
                    ? `M ${x} ${y} L ${x + w - r} ${y} Q ${x + w} ${y} ${x + w} ${y + r} L ${x + w} ${y + h}`
                    : q.key === "bottomLeft"
                      ? `M ${x} ${y} L ${x} ${y + h - r} Q ${x} ${y + h} ${x + r} ${y + h} L ${x + w} ${y + h}`
                      : `M ${x} ${y + h} L ${x + w - r} ${y + h} Q ${x + w} ${y + h} ${x + w} ${y + h - r} L ${x + w} ${y}`;
              return (
                <g key={q.key}>
                  <rect
                    x={x}
                    y={y}
                    width={Math.max(0, w)}
                    height={Math.max(0, h)}
                    rx={r}
                    ry={r}
                    fill="#ffffff"
                    stroke="none"
                    style={{ cursor: "pointer" }}
                    onClick={e => {
                      e.stopPropagation();
                      setActiveQuadrant(prev => {
                        const next = prev === q.key ? null : q.key;
                        if (next) {
                          const ids = points
                            .filter(p => {
                              const pk: QuadrantKey =
                                p.x < midX
                                  ? p.y < midY
                                    ? "topLeft"
                                    : "bottomLeft"
                                  : p.y < midY
                                    ? "topRight"
                                    : "bottomRight";
                              return pk === next;
                            })
                            .map(p => p.account.account_id);
                          onSelectedAccountIdsChange(ids);
                        } else {
                          onSelectedAccountIdsChange([]);
                        }
                        return next;
                      });
                    }}
                  />
                  {!isActive && (
                    <path
                      d={outerPath}
                      fill="none"
                      stroke={color}
                      strokeWidth={QUADRANT_STROKE}
                      strokeLinecap="round"
                      pointerEvents="none"
                    />
                  )}
                </g>
              );
            })}

          {/* Dashed mid-axis crosshair (center divider). */}
          {!showGuide && (
            <g pointerEvents="none" aria-hidden>
              {(() => {
                const o = QUADRANT_OUTER_INSET;
                return (
                  <>
                    <line
                      x1={plotL + o}
                      y1={midY}
                      x2={plotL + plotW - o}
                      y2={midY}
                      stroke={MID_AXIS_STROKE}
                      strokeWidth={1}
                      strokeDasharray={MID_AXIS_DASH}
                      strokeLinecap="butt"
                    />
                    <line
                      x1={midX}
                      y1={plotT + o}
                      x2={midX}
                      y2={plotBottom - o}
                      stroke={MID_AXIS_STROKE}
                      strokeWidth={1}
                      strokeDasharray={MID_AXIS_DASH}
                      strokeLinecap="butt"
                    />
                  </>
                );
              })()}
            </g>
          )}

          {/* Active quadrant frame — solid purple; removed when Clear empties selection. */}
          {!showGuide &&
            activeQuadrant &&
            (() => {
              const q = quadrantBoxes.find(b => b.key === activeQuadrant);
              if (!q) return null;
              return (
                <rect
                  key={`active-${q.key}`}
                  x={q.x}
                  y={q.y}
                  width={Math.max(0, q.w)}
                  height={Math.max(0, q.h)}
                  rx={QUADRANT_RADIUS}
                  ry={QUADRANT_RADIUS}
                  fill="none"
                  stroke="#7C3AED"
                  strokeWidth={QUADRANT_STROKE_ACTIVE}
                  pointerEvents="none"
                />
              );
            })()}

          {/* Axis spines + △ heads — above fills so arrows stay visible.
              When View guide is open these sit under the frost, so a top SVG
              layer re-draws them (see below). */}
          {!showGuide && renderAxisSpines()}

          {/* Soft 25 / 75 gridlines — clipped inside each colored frame so they
              never cross the border stroke or the main axis gap. */}
          <g pointerEvents="none">
            {(() => {
              const pad = QUADRANT_STROKE + 1;
              const lines: React.ReactNode[] = [];
              xTicks.forEach(tick => {
                if (
                  tick === lens.xMin ||
                  tick === lens.xMax ||
                  tick === lens.xMid
                ) {
                  return;
                }
                const x = plotX(tick);
                quadrantBoxes.forEach(box => {
                  if (x <= box.x + pad || x >= box.x + box.w - pad) return;
                  lines.push(
                    <line
                      key={`xg-${tick}-${box.key}`}
                      x1={x}
                      y1={box.y + pad}
                      x2={x}
                      y2={box.y + box.h - pad}
                      stroke="#f1f5f9"
                      strokeWidth={1}
                    />
                  );
                });
              });
              yTicks.forEach(tick => {
                if (
                  tick === lens.yMin ||
                  tick === lens.yMax ||
                  tick === lens.yMid
                ) {
                  return;
                }
                const y = plotY(tick);
                quadrantBoxes.forEach(box => {
                  if (y <= box.y + pad || y >= box.y + box.h - pad) return;
                  lines.push(
                    <line
                      key={`yg-${tick}-${box.key}`}
                      x1={box.x + pad}
                      y1={y}
                      x2={box.x + box.w - pad}
                      y2={y}
                      stroke="#f1f5f9"
                      strokeWidth={1}
                    />
                  );
                });
              });
              return lines;
            })()}
          </g>

          {/* Axis-end low/high labels (mock data-axis-end/data-axis-y-end
              text, ~5790-5795: font-size 8.5, fill #9ca3af, font-weight 700). */}
          <text
            x={plotL}
            y={plotBottom + 32}
            textAnchor="start"
            fontSize={8.5}
            fontWeight={700}
            fill="#9ca3af"
          >
            {lens.xLowLabel}
          </text>
          <text
            x={xHighCaptionX}
            y={plotBottom + 32}
            textAnchor="end"
            fontSize={8.5}
            fontWeight={700}
            fill="#9ca3af"
          >
            {lens.xHighLabel}
          </text>
          <text
            x={yCaptionX}
            y={plotT + 15}
            textAnchor="end"
            fontSize={8.5}
            fontWeight={700}
            fill="#9ca3af"
          >
            {lens.yHighLabel}
          </text>
          <text
            x={yCaptionX}
            y={plotBottom - 10}
            textAnchor="end"
            fontSize={8.5}
            fontWeight={700}
            fill="#9ca3af"
          >
            {lens.yLowLabel}
          </text>

          {/* Axis titles with Lucide ArrowRight. X: title →. Y: [title][→]
              then rotate −90° so the icon sits at High BH and points up. */}
          <foreignObject
            x={plotL}
            y={plotBottom + 40}
            width={plotW}
            height={16}
          >
            <div className="flex h-full items-center justify-center gap-0.5 text-[10px] font-bold leading-none text-[#9ca3af]">
              <span>{lens.xAxisTitle.replace(/\s*→\s*$/, "").trim()}</span>
              <ArrowRight
                className="h-2.5 w-2.5 shrink-0"
                strokeWidth={2.75}
                aria-hidden
              />
            </div>
          </foreignObject>
          <foreignObject
            x={Y_AXIS_TITLE_INSET}
            y={plotT}
            width={26}
            height={plotH}
          >
            <div className="flex h-full w-full items-center justify-center">
              <div
                className="flex items-center gap-0.5 whitespace-nowrap text-[10px] font-bold leading-none text-[#9ca3af]"
                style={{ transform: "rotate(-90deg)" }}
              >
                <span>{lens.yAxisTitle}</span>
                <ArrowRight
                  className="h-2.5 w-2.5 shrink-0"
                  strokeWidth={2.75}
                  aria-hidden
                />
              </div>
            </div>
          </foreignObject>

          {[...points]
            .sort((a, b) => {
              const aSel = a.stack.some(acc =>
                selectedIdSet.has(acc.account_id)
              )
                ? 1
                : 0;
              const bSel = b.stack.some(acc =>
                selectedIdSet.has(acc.account_id)
              )
                ? 1
                : 0;
              return (
                aSel - bSel ||
                a.account.account_id.localeCompare(b.account.account_id)
              );
            })
            .map(p => {
              const r = dotRadius(p.account, { signalsVisible });
              const stackCount = p.stack.length;
              const isSelected = p.stack.some(acc =>
                selectedIdSet.has(acc.account_id)
              );
              const stroke =
                BH_CLASS_STROKE[p.account.bh_class] ?? BH_CLASS_STROKE.no_data;
              const fill =
                BH_CLASS_FILL[p.account.bh_class] ?? BH_CLASS_FILL.no_data;
              const halo = p.account.top_signal_tier
                ? TIER_HALO[p.account.top_signal_tier]
                : undefined;
              const quadrantKey: QuadrantKey =
                p.x < midX
                  ? p.y < midY
                    ? "topLeft"
                    : "bottomLeft"
                  : p.y < midY
                    ? "topRight"
                    : "bottomRight";
              const dimmed =
                activeQuadrant != null && quadrantKey !== activeQuadrant;
              const stackKey = p.stack
                .map(acc => acc.account_id)
                .sort()
                .join("|");
              return (
                <g
                  key={stackKey}
                  data-scatter-node="true"
                  opacity={showGuide ? 0.16 : dimmed ? 0.2 : 1}
                  style={{ cursor: "pointer" }}
                  onClick={e => {
                    e.stopPropagation();
                    if (stackCount > 1 && !e.shiftKey) {
                      setHoverId(null);
                      setOverlapStack(p.stack);
                      return;
                    }
                    if (e.shiftKey) {
                      const next = new Set(selectedAccountIds);
                      if (next.has(p.account.account_id)) {
                        next.delete(p.account.account_id);
                      } else {
                        next.add(p.account.account_id);
                      }
                      onSelectedAccountIdsChange(Array.from(next));
                    } else {
                      onSelectedAccountIdsChange([p.account.account_id]);
                    }
                  }}
                  onMouseEnter={e => {
                    if (showGuide) return;
                    setHoverId(p.account.account_id);
                    setHoverPos({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseMove={e => {
                    if (showGuide) return;
                    setHoverPos({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseLeave={() => setHoverId(null)}
                >
                  {/* Halo ring — critical/warning only (mock 5823-5827: r+2.5,
                    stroke-width 1.3, opacity .68). */}
                  {halo && (
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={r + 2.5}
                      fill="none"
                      stroke={halo}
                      strokeWidth={1.3}
                      opacity={0.68}
                      pointerEvents="none"
                    />
                  )}
                  {/* Selection ring (mock 5830-5832: r+5, solid, brand color,
                    stroke-width 2.5, opacity .9 — not a dashed purple ring). */}
                  {isSelected && (
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={r + 5}
                      fill="none"
                      stroke="#9333EA"
                      strokeWidth={2.5}
                      opacity={0.9}
                      pointerEvents="none"
                    />
                  )}
                  {/* Main dot: fill/stroke = pmBhFill/pmBhColor, stroke-width 1.8
                    (mock 5836-5842). */}
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={r}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={1.8}
                  />
                  {/* Initials — hidden while guide is open so overlay text stays readable. */}
                  {!showGuide && (
                    <text
                      x={p.x}
                      y={p.y + 2.7}
                      textAnchor="middle"
                      fontSize={r >= 10 ? 7.5 : 6.5}
                      fontWeight={900}
                      fill={stroke}
                      pointerEvents="none"
                    >
                      {accountInitials(p.account.account_name)}
                    </text>
                  )}
                  {/* Overlap stack badge — purple +N when multiple accounts share
                    the same Fit / score position. */}
                  {stackCount > 1 && !showGuide && (
                    <g pointerEvents="none">
                      <circle
                        cx={p.x + r * 0.72}
                        cy={p.y - r * 0.72}
                        r={8}
                        fill="#9333EA"
                        stroke="#fff"
                        strokeWidth={1.5}
                      />
                      <text
                        x={p.x + r * 0.72}
                        y={p.y - r * 0.72 + 3.2}
                        textAnchor="middle"
                        fontSize={8}
                        fontWeight={800}
                        fill="#fff"
                      >
                        +{stackCount - 1}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}

          {/* Availability-gutter bubbles — dashed stroke, BH coloring when
              known (mock drawUnavailableBubble ~6222-6239). */}
          {gutterPoints.map(p => {
            const r = Math.min(
              dotRadius(p.account, { signalsVisible }),
              signalsVisible ? 9 : 11
            );
            const isSelected = selectedIdSet.has(p.account.account_id);
            const hasBh = p.account.bh != null && Number.isFinite(p.account.bh);
            const stroke = hasBh
              ? (BH_CLASS_STROKE[p.account.bh_class] ?? BH_CLASS_STROKE.no_data)
              : "#64748b";
            const fill = hasBh
              ? (BH_CLASS_FILL[p.account.bh_class] ?? BH_CLASS_FILL.no_data)
              : "#eef1f5";
            return (
              <g
                key={`gutter-${p.account.account_id}`}
                data-scatter-node="true"
                opacity={showGuide ? 0.16 : 1}
                style={{ cursor: "pointer" }}
                onClick={e => {
                  e.stopPropagation();
                  if (e.shiftKey) {
                    const next = new Set(selectedAccountIds);
                    if (next.has(p.account.account_id)) {
                      next.delete(p.account.account_id);
                    } else {
                      next.add(p.account.account_id);
                    }
                    onSelectedAccountIdsChange(Array.from(next));
                  } else {
                    onSelectedAccountIdsChange([p.account.account_id]);
                  }
                }}
                onMouseEnter={e => {
                  if (showGuide) return;
                  setHoverId(p.account.account_id);
                  setHoverPos({ x: e.clientX, y: e.clientY });
                }}
                onMouseMove={e => {
                  if (showGuide) return;
                  setHoverPos({ x: e.clientX, y: e.clientY });
                }}
                onMouseLeave={() => setHoverId(null)}
              >
                {isSelected && (
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={r + 4}
                    fill="none"
                    stroke="#9333EA"
                    strokeWidth={2.5}
                    opacity={0.9}
                    pointerEvents="none"
                  />
                )}
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={r}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={1.8}
                  strokeDasharray="3 2"
                />
                {!showGuide && (
                  <text
                    x={p.x}
                    y={p.y + 2.6}
                    textAnchor="middle"
                    fontSize={6.5}
                    fontWeight={900}
                    fill={stroke}
                    pointerEvents="none"
                  >
                    {accountInitials(p.account.account_name)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Guide open: overlays + click anywhere on the graph to close. */}
        {showGuide && overlayCells && (
          <div
            className="absolute inset-0 z-[5] cursor-pointer"
            onClick={() => setShowGuide(false)}
            role="button"
            tabIndex={0}
            aria-label="Close guide"
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setShowGuide(false);
              }
            }}
          >
            <div
              className="pointer-events-none absolute bg-white/38 backdrop-blur-[4px]"
              style={plotPct}
              aria-hidden
            />
            <div className="pointer-events-none absolute" style={plotPct}>
              {(() => {
                const o = 0.75; // % outer inset — match QUADRANT_OUTER_INSET
                return (
                  <>
                    {renderQuadrantOverlay("topLeft", overlayCells.topLeft, {
                      left: `${o}%`,
                      top: `${o}%`,
                      width: `${50 - o}%`,
                      height: `${50 - o}%`,
                    })}
                    {renderQuadrantOverlay("topRight", overlayCells.topRight, {
                      left: "50%",
                      top: `${o}%`,
                      width: `${50 - o}%`,
                      height: `${50 - o}%`,
                    })}
                    {renderQuadrantOverlay(
                      "bottomLeft",
                      overlayCells.bottomLeft,
                      {
                        left: `${o}%`,
                        top: "50%",
                        width: `${50 - o}%`,
                        height: `${50 - o}%`,
                      }
                    )}
                    {renderQuadrantOverlay(
                      "bottomRight",
                      overlayCells.bottomRight,
                      {
                        left: "50%",
                        top: "50%",
                        width: `${50 - o}%`,
                        height: `${50 - o}%`,
                      }
                    )}
                    {/* Dashed mid-axes (same as scatter — no solid center borders). */}
                    <div
                      aria-hidden
                      className="absolute border-t border-dashed border-[#cbd5e1]"
                      style={{
                        left: `${o}%`,
                        top: "50%",
                        width: `${100 - 2 * o}%`,
                        transform: "translateY(-50%)",
                      }}
                    />
                    <div
                      aria-hidden
                      className="absolute border-l border-dashed border-[#cbd5e1]"
                      style={{
                        left: "50%",
                        top: `${o}%`,
                        height: `${100 - 2 * o}%`,
                        transform: "translateX(-50%)",
                      }}
                    />
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Re-draw X/Y arrows above the guide frost so they stay visible. */}
        {showGuide && (
          <svg
            className="pointer-events-none absolute inset-0 z-[6] h-full w-full"
            viewBox={`0 0 ${RW} ${RH}`}
            preserveAspectRatio="xMidYMid meet"
            aria-hidden
          >
            {renderAxisSpines("#9ca3af")}
          </svg>
        )}

        {points.length === 0 &&
          gutterPoints.length === 0 &&
          unscoredAccounts.length === 0 && (
            <div className="absolute inset-0 z-[6] flex items-center justify-center bg-slate-50/50 p-4">
              <div className="w-full max-w-[380px] rounded-xl border border-slate-200/90 bg-white px-6 py-7 text-center shadow-sm">
                <h3 className="mb-2 text-[15px] font-semibold text-slate-900">
                  No accounts to plot yet
                </h3>
                <p className="mb-0 text-[13px] text-slate-500">
                  Accounts need public-data scores before they appear here.
                </p>
              </div>
            </div>
          )}

        {hoverPoint && hoverPos && (
          <div
            className="pointer-events-none fixed z-50 w-[min(100vw-16px,260px)] rounded-lg border border-slate-200 bg-white p-2.5 shadow-xl"
            style={{ left: hoverPos.x + 14, top: hoverPos.y + 14 }}
          >
            <p className="mb-1 truncate text-sm font-bold text-slate-900">
              {hoverPoint.account.account_name}
              {"stack" in hoverPoint &&
              Array.isArray(hoverPoint.stack) &&
              hoverPoint.stack.length > 1
                ? ` (+${hoverPoint.stack.length - 1} overlapping)`
                : ""}
            </p>
            <p className="mb-1 text-[11px] text-slate-500">
              Fit {Math.round(hoverPoint.account.fit ?? 0)} · BH{" "}
              {hoverPoint.account.bh != null
                ? Math.round(hoverPoint.account.bh)
                : "—"}{" "}
              ({hoverPoint.account.bh_class.replace("_", " ")}) · AH{" "}
              {hoverPoint.account.ah != null && hoverPoint.account.ah > 0
                ? Math.round(hoverPoint.account.ah)
                : "—"}
              {(() => {
                // Net Investment uses public R&O; AH Risk × Opportunity uses KPI.
                const usePublic =
                  lens.key === "net_signal_fit" ||
                  hoverPoint.account.public_risk != null ||
                  hoverPoint.account.public_opportunity != null;
                const risk =
                  lens.key === "cluster"
                    ? hoverPoint.account.risk
                    : usePublic
                      ? (hoverPoint.account.public_risk ??
                        hoverPoint.account.risk)
                      : hoverPoint.account.risk;
                const opp =
                  lens.key === "cluster"
                    ? hoverPoint.account.opportunity
                    : usePublic
                      ? (hoverPoint.account.public_opportunity ??
                        hoverPoint.account.opportunity)
                      : hoverPoint.account.opportunity;
                const net = hoverPoint.account.net_investment_signal;
                const parts: string[] = [
                  `Risk ${risk != null ? Math.round(risk) : "—"}`,
                  `Opp ${opp != null ? Math.round(opp) : "—"}`,
                ];
                if (lens.key === "net_signal_fit" && net != null) {
                  parts.push(`Net ${Math.round(net)}`);
                }
                return ` · ${parts.join(" · ")}`;
              })()}
              {hoverPoint.account.rl_priority
                ? ` · RL Priority: ${RL_PRIORITY_LABEL[hoverPoint.account.rl_priority] ?? hoverPoint.account.rl_priority}`
                : ""}
            </p>
            {hoverPoint.account.active_signal_count > 0 && (
              <p className="mb-0.5 text-[11px] text-slate-500">
                {hoverPoint.account.active_signal_count} active signal
                {hoverPoint.account.active_signal_count === 1 ? "" : "s"}:{" "}
                {tierBreakdown(hoverPoint.account.tier_counts)}
              </p>
            )}
            {hoverPoint.account.top_signal_key && (
              <p className="mb-0 text-[11px] text-slate-500">
                Top signal: {signalTitle(hoverPoint.account.top_signal_key)}
              </p>
            )}
          </div>
        )}
      </div>

      {unscoredAccounts.length > 0 && (
        <div className="inline-flex max-w-full flex-wrap items-center gap-1.5 rounded-md border border-dashed border-gray-300 bg-gray-50 px-2.5 py-2">
          <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-gray-400">
            Not yet scored ({unscoredAccounts.length})
          </span>
          {unscoredAccounts.map(a => {
            const isSelected = selectedIdSet.has(a.account_id);
            return (
              <button
                key={a.account_id}
                type="button"
                title={`${a.account_name} — shift-click to add to comparison`}
                onClick={e => {
                  if (e.shiftKey) {
                    const next = new Set(selectedAccountIds);
                    if (next.has(a.account_id)) next.delete(a.account_id);
                    else next.add(a.account_id);
                    onSelectedAccountIdsChange(Array.from(next));
                  } else {
                    onSelectedAccountIdsChange([a.account_id]);
                  }
                }}
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[9px] font-extrabold ${
                  isSelected
                    ? "border-purple-600 bg-purple-100 text-purple-700"
                    : "border-dashed border-gray-400 bg-white text-gray-500 hover:border-gray-600"
                }`}
              >
                {accountInitials(a.account_name)}
              </button>
            );
          })}
        </div>
      )}

      <PortfolioMapGuidePanel
        lensKey={lens.key}
        cells={guideCells}
        showGuide={showGuide}
        onShowGuideChange={setShowGuide}
        showToggle={false}
        showLegend
        hideCardGrid
      />

      <PortfolioMapOverlapModal
        open={overlapStack != null && overlapStack.length > 1}
        accounts={overlapStack ?? []}
        onClose={() => setOverlapStack(null)}
        onSelect={accountId => {
          onSelectedAccountIdsChange([accountId]);
          setOverlapStack(null);
        }}
      />
    </div>
  );
};

export default PortfolioMapScatter;
